import {readSearchMarket,matchesMarket} from "../utils/market";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db as defaultDb } from "../firebase";
import BusinessResultCard from "../components/BusinessResultCard";

const PAGE_SIZE = 18;
const FETCH_TIMEOUT_MS = 8000;

function normalizeText(s) {
  if (s == null) return "";
  try {
    return String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  } catch {
    return String(s).toLowerCase().trim();
  }
}

function parseLocParam(search) {
  const sp = new URLSearchParams(search || "");
  return sp.get("loc") || "";
}

function uniq(arr) {
  const out = [];
  const seen = new Set();
  for (const x of arr) {
    const k = String(x || "");
    if (!k) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

function extractBizLocationParts(b) {
  const province = b.province || b.provincia || "";
  const district = b.district || b.distrito || "";
  const corregimiento = b.corregimiento || "";
  const zone = b.zone || b.zona || "";

  return {
    province: normalizeText(province),
    district: normalizeText(district),
    corregimiento: normalizeText(corregimiento),
    zone: normalizeText(zone),
    address: normalizeText(b.address || b.locationString || b.addressLine || ""),
    city: normalizeText(b.city || b.cityName || b.locationCity || ""),
    coverageArea: normalizeText(b.coverageArea || ""),
  };
}

function businessMatchesLocation(b, locLabel) {
  const label = normalizeText(locLabel);
  if (!label) return true;

  let chunks = label
    .split(",")
    .map((x) => normalizeText(x))
    .filter(Boolean);

  chunks = uniq(chunks).filter((c) => c !== "panama" && c !== "panamá");

  const p = extractBizLocationParts(b);
  const hasAny = p.province || p.district || p.corregimiento || p.zone || p.address || p.city || p.coverageArea;
  if (!hasAny) return false;

  const fullText = [p.zone, p.corregimiento, p.district, p.province, p.address, p.city, p.coverageArea].filter(Boolean).join(" ");
  const serviceAreas = Array.isArray(b?.serviceAreas) ? b.serviceAreas : [];
  const serviceAreasAdvanced = Array.isArray(b?.serviceAreasAdvanced) ? b.serviceAreasAdvanced : [];
  const areaText = [...serviceAreas, ...serviceAreasAdvanced]
    .map((area) => typeof area === "string" ? area : [area?.label, area?.zone, area?.corregimiento, area?.district, area?.province].filter(Boolean).join(" "))
    .map(normalizeText)
    .join(" ");

  if (chunks.length === 0) return true;
  return chunks.some((c) => fullText.includes(c) || areaText.includes(c));
}

function errorToMessage(e) {
  try {
    if (!e) return "";
    if (typeof e === "string") return e;
    if (e.code && e.message) return `${e.code}: ${e.message}`;
    if (e.message) return e.message;
    return String(e);
  } catch {
    return "Unknown error";
  }
}

function withTimeout(promise, ms, label = "timeout") {
  let to = null;
  const timeoutPromise = new Promise((_, reject) => {
    to = setTimeout(() => reject(new Error(`${label} after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (to) clearTimeout(to);
  });
}

function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return biz.visible !== false && biz.hidden !== true && biz.isActive !== false && biz.published !== false;
}


function locationRelatedChips(locLabel, filterOn) {
  const base = ["Belleza & Barberías", "Uñas", "Masajes", "A domicilio", "San Francisco", "Costa del Este"];
  if (filterOn) return ["Todos", "En local", "A domicilio", "Con reseñas", ...base].slice(0, 7);
  const label = normalizeText(locLabel);
  if (label.includes("costa")) return ["San Francisco", "Paitilla", "Bella Vista", "Uñas", "Masajes", "Belleza & Barberías"];
  if (label.includes("san francisco")) return ["Costa del Este", "Paitilla", "Bella Vista", "Barbería", "Spa", "A domicilio"];
  return base;
}

export default function LocationResultsPage({ db = defaultDb }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const locLabel = useMemo(() => parseLocParam(loc.search), [loc.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [modeFilter, setModeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recommended");
  const [onlyRated, setOnlyRated] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setError(null);
    setItems([]);

    try {
      const col = collection(db, "businesses");
      const q1 = query(col, ...(readSearchMarket().code === "PA" ? [] : [where("countryCode","==",readSearchMarket().code)]), where("status", "==", "active"), limit(PAGE_SIZE * 12));
      const snap = await withTimeout(getDocs(q1), FETCH_TIMEOUT_MS, "getDocs(businesses active)");
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter(isPublicBusiness).filter(b => matchesMarket(b));
      setItems(arr.filter((b) => businessMatchesLocation(b, locLabel)));
    } catch (e) {
      console.error("[LocationResultsPage] loadFirst error", e);
      setError(errorToMessage(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [db, locLabel]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const filteredItems = useMemo(() => {
    const ratingValue = (b) => Number(b?.rating || b?.averageRating || b?.stars || 0) || 0;
    const isDelivery = (b) => {
      const mode = String(b?.serviceMode || b?.mode || "").toLowerCase();
      return mode.includes("delivery") || mode.includes("domicilio") || mode.includes("hybrid") || mode.includes("mixto") || (Array.isArray(b?.serviceAreas) && b.serviceAreas.length > 0) || (Array.isArray(b?.serviceAreasAdvanced) && b.serviceAreasAdvanced.length > 0);
    };
    const isLocal = (b) => {
      const mode = String(b?.serviceMode || b?.mode || "").toLowerCase();
      return !mode || mode.includes("onsite") || mode.includes("local") || mode.includes("hybrid") || mode.includes("mixto");
    };

    let list = items.slice();
    if (modeFilter === "delivery") list = list.filter(isDelivery);
    if (modeFilter === "onsite") list = list.filter(isLocal);
    if (onlyRated) list = list.filter((b) => ratingValue(b) > 0);

    if (sortBy === "rating") list.sort((a, b) => ratingValue(b) - ratingValue(a));
    else if (sortBy === "name") list.sort((a, b) => String(a?.name || a?.businessName || "").localeCompare(String(b?.name || b?.businessName || "")));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || ratingValue(b) - ratingValue(a));
    return list;
  }, [items, modeFilter, onlyRated, sortBy]);

  const activeFilterCount = [modeFilter !== "all", onlyRated, sortBy !== "recommended"].filter(Boolean).length;
  const resetFilters = () => {
    setModeFilter("all");
    setOnlyRated(false);
    setSortBy("recommended");
  };

  return (
    <div className="lr-page">
      <style>{styles}</style>
      <main className="lr-shell">
        <section className="lr-hero">
          <div className="lr-topline">
            <button type="button" className="lr-back" onClick={() => navigate(-1)}>← Volver</button>
            <span className="lr-count">{loading ? "Buscando..." : `${filteredItems.length} negocios`}</span>
          </div>

          <div className="lr-title-block">
            <span>Resultados por ubicación</span>
            <h1>{locLabel || "Ubicación"}</h1>
            <p>Explora empresas que atienden esta zona o tienen presencia cerca. Filtra por atención en local o a domicilio.</p>
          </div>

          <div className="lr-filters lr-premium-filters" aria-label="Filtros por modo de atención">
            <button type="button" className="lr-filter-master" onClick={() => setFilterSheetOpen(true)}>Filtros{activeFilterCount ? ` · ${activeFilterCount}` : ""}</button>
            <button type="button" className={modeFilter === "all" ? "active" : ""} onClick={() => setModeFilter("all")}>Todos</button>
            <button type="button" className={modeFilter === "delivery" ? "active" : ""} onClick={() => setModeFilter("delivery")}>A domicilio</button>
            <button type="button" className={modeFilter === "onsite" ? "active" : ""} onClick={() => setModeFilter("onsite")}>En local</button>
            <button type="button" className={onlyRated ? "active" : ""} onClick={() => setOnlyRated((v) => !v)}>Con reseñas</button>
            <button type="button" className={sortBy === "rating" ? "active" : ""} onClick={() => setSortBy(sortBy === "rating" ? "recommended" : "rating")}>Mejor valorados</button>
            {activeFilterCount ? <button type="button" className="lr-clear-filter" onClick={resetFilters}>Limpiar</button> : null}
          </div>
        </section>

        {filterSheetOpen ? (
          <div className="lr-filter-sheet" role="dialog" aria-modal="true" aria-label="Filtros de ubicación">
            <button type="button" className="lr-filter-backdrop" onClick={() => setFilterSheetOpen(false)} aria-label="Cerrar filtros" />
            <div className="lr-filter-panel">
              <div className="lr-filter-head">
                <div><span>Refinar ubicación</span><strong>{filteredItems.length} negocios</strong></div>
                <button type="button" onClick={() => setFilterSheetOpen(false)}>Cerrar</button>
              </div>
              <h4>Modo de atención</h4>
              <div className="lr-sheet-options">
                <button type="button" className={modeFilter === "all" ? "active" : ""} onClick={() => setModeFilter("all")}>Todos</button>
                <button type="button" className={modeFilter === "delivery" ? "active" : ""} onClick={() => setModeFilter("delivery")}>A domicilio</button>
                <button type="button" className={modeFilter === "onsite" ? "active" : ""} onClick={() => setModeFilter("onsite")}>En local</button>
                <button type="button" className={onlyRated ? "active" : ""} onClick={() => setOnlyRated((v) => !v)}>Con reseñas</button>
              </div>
              <h4>Ordenar</h4>
              <div className="lr-sheet-options">
                <button type="button" className={sortBy === "recommended" ? "active" : ""} onClick={() => setSortBy("recommended")}>Recomendados</button>
                <button type="button" className={sortBy === "rating" ? "active" : ""} onClick={() => setSortBy("rating")}>Mejor valorados</button>
                <button type="button" className={sortBy === "name" ? "active" : ""} onClick={() => setSortBy("name")}>Nombre</button>
              </div>
              <div className="lr-filter-actions">
                <button type="button" className="secondary" onClick={resetFilters}>Limpiar</button>
                <button type="button" className="primary" onClick={() => setFilterSheetOpen(false)}>Ver resultados</button>
              </div>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="lr-error">
            <strong>No pudimos cargar empresas.</strong>
            <span>{error}</span>
            <button type="button" onClick={loadFirst}>Reintentar</button>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <div className="lr-loading">
            {[0, 1, 2].map((i) => <span key={i} />)}
          </div>
        ) : null}

        {!loading && !error && filteredItems.length === 0 ? (
          <div className="lr-empty lr-smart-empty">
            <span className="lr-empty-kicker">Sugerencias para continuar</span>
            <strong>{items.length > 0 ? "No hay resultados con estos filtros." : "No encontramos negocios en esta zona todavía."}</strong>
            <p>{items.length > 0 ? "Puedes limpiar los filtros o probar un modo de atención diferente." : "Prueba una zona cercana, una categoría popular o explora todo el marketplace."}</p>
            <div className="lr-empty-chips">
              {locationRelatedChips(locLabel, activeFilterCount > 0).map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => {
                    if (chip === "Todos") resetFilters();
                    else if (chip === "En local") setModeFilter("onsite");
                    else if (chip === "A domicilio") setModeFilter("delivery");
                    else if (chip === "Con reseñas") setOnlyRated(true);
                    else if (["San Francisco", "Costa del Este", "Paitilla", "Bella Vista"].includes(chip)) navigate(`/location-results?loc=${encodeURIComponent(chip)}`);
                    else navigate(`/services/search?q=${encodeURIComponent(chip)}`);
                  }}
                >
                  {chip}
                </button>
              ))}
            </div>
            <div className="lr-empty-actions">
              {activeFilterCount ? <button type="button" onClick={resetFilters}>Limpiar filtros</button> : null}
              <button type="button" onClick={() => navigate("/services/search?q=servicios")}>Explorar servicios</button>
            </div>
          </div>
        ) : null}

        <section className="lr-list" aria-label="Negocios encontrados por ubicación">
          {filteredItems.map((b, index) => (
            <BusinessResultCard
              key={b.id}
              biz={b}
              priority={index < 4}
              variant="list"
              contextLabel="Cerca de tu zona"
              actionLabel="Reservar"
              onOpenBooking={() => navigate(`/booking?businessId=${encodeURIComponent(b.id)}`)}
              onOpenBusiness={() => navigate(`/business/${b.id}`)}
            />
          ))}
        </section>
      </main>
    </div>
  );
}

const styles = `
  .lr-page { min-height: 100vh; background: radial-gradient(circle at 8% 0%, rgba(0,103,95,.085), transparent 28%), linear-gradient(180deg,#fff 0%,#fbfdfd 44%,#fff 100%); color: #0f172a; }
  .lr-shell { width: min(100%, 1160px); margin: 0 auto; padding: 28px clamp(16px,4vw,48px) 64px; box-sizing: border-box; }
  .lr-hero { display: grid; gap: 16px; padding: 18px 0 22px; border-bottom: 1px solid rgba(203,213,225,.72); margin-bottom: 18px; }
  .lr-topline { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .lr-back { border: 1px solid rgba(226,232,240,.95); background: rgba(255,255,255,.86); color: #334155; border-radius: 999px; min-height: 40px; padding: 0 14px; font-size: 13px; font-weight: 950; box-shadow: 0 10px 24px rgba(15,23,42,.045); cursor: pointer; }
  .lr-count { border: 1px solid rgba(0,103,95,.13); background: rgba(236,253,245,.82); color: #00675f; border-radius: 999px; min-height: 38px; padding: 0 14px; display: inline-flex; align-items: center; font-size: 12px; font-weight: 950; white-space: nowrap; }
  .lr-title-block span { display: inline-flex; width: fit-content; border: 1px solid rgba(0,103,95,.16); background: rgba(236,253,245,.82); color: #00675f; border-radius: 999px; padding: 7px 11px; font-size: 11px; font-weight: 950; letter-spacing: .10em; text-transform: uppercase; margin-bottom: 12px; }
  .lr-title-block h1 { margin: 0; color: #07111f; font-size: clamp(40px,5vw,68px); line-height: .9; letter-spacing: -.082em; font-weight: 950; }
  .lr-title-block p { margin: 14px 0 0; max-width: 680px; color: #64748b; font-size: 16px; line-height: 1.5; font-weight: 680; }
  .lr-filters { display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none; }
  .lr-filters::-webkit-scrollbar { display: none; }
  .lr-filters button { flex: 0 0 auto; border: 1px solid rgba(226,232,240,.95); background: #fff; color: #334155; border-radius: 999px; min-height: 38px; padding: 0 14px; font-size: 13px; font-weight: 900; box-shadow: 0 8px 20px rgba(15,23,42,.04); cursor: pointer; }
  .lr-filters button.active { background: #00675f; color: #fff; border-color: #00675f; box-shadow: 0 14px 28px rgba(0,103,95,.18); }
  .lr-list { display: grid; gap: 14px; }

  .lr-smart-empty { align-items: start; background: radial-gradient(circle at 12% 0%, rgba(20,184,166,.12), transparent 28%), rgba(255,255,255,.96) !important; }
  .lr-empty-kicker { width: fit-content; border: 1px solid rgba(0,103,95,.16); background: rgba(236,253,245,.82); color: #00675f; border-radius: 999px; padding: 7px 11px; font-size: 11px; font-weight: 950; letter-spacing: .10em; text-transform: uppercase; }
  .lr-empty-chips { display:flex; gap:8px; flex-wrap:wrap; }
  .lr-empty-chips button { border:1px solid rgba(0,103,95,.14); background:#fff; color:#0f766e; border-radius:999px; min-height:36px; padding:0 13px; font-size:12.5px; font-weight:900; cursor:pointer; box-shadow:0 8px 20px rgba(15,23,42,.04); }
  .lr-empty-actions { display:flex; gap:10px; flex-wrap:wrap; }

  .lr-error, .lr-empty { border: 1px solid rgba(226,232,240,.92); background: rgba(255,255,255,.92); border-radius: 30px; padding: 24px; box-shadow: 0 18px 48px rgba(15,23,42,.055); color: #64748b; font-weight: 800; margin-bottom: 14px; display: grid; gap: 10px; }
  .lr-error strong, .lr-empty strong { color: #0f172a; font-size: 18px; }
  .lr-error button, .lr-empty button { width: fit-content; border: 0; background: #00675f; color: #fff; border-radius: 999px; min-height: 42px; padding: 0 16px; font-weight: 950; cursor: pointer; }
  .lr-loading { display: grid; gap: 12px; }
  .lr-loading span { height: 126px; border-radius: 26px; background: linear-gradient(90deg,#f1f5f9 0%,#f8fafc 50%,#f1f5f9 100%); background-size: 200% 100%; animation: lrShimmer 1.25s infinite linear; }
  @keyframes lrShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
  @media (max-width: 720px) { .lr-shell { padding: 16px 14px calc(96px + env(safe-area-inset-bottom,0px)); } .lr-title-block h1 { font-size: clamp(36px,10.8vw,48px); } .lr-title-block p { font-size: 14px; } }
  .lr-filter-master { background:#0f172a!important; color:#fff!important; border-color:#0f172a!important; box-shadow:0 14px 30px rgba(15,23,42,.16)!important; }
  .lr-clear-filter { color:#dc2626!important; border-color:#fecaca!important; background:#fff!important; }
  .lr-filter-sheet{position:fixed;inset:0;z-index:100020;display:flex;align-items:flex-end;justify-content:center;}
  .lr-filter-backdrop{position:absolute;inset:0;border:0;background:rgba(15,23,42,.38);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);}
  .lr-filter-panel{position:relative;width:min(100%,680px);max-height:min(82vh,680px);overflow-y:auto;background:#fff;border-radius:30px 30px 0 0;padding:18px 18px calc(18px + env(safe-area-inset-bottom,0px));box-shadow:0 -26px 70px rgba(15,23,42,.22);}
  .lr-filter-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;}
  .lr-filter-head span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;}
  .lr-filter-head strong{display:block;margin-top:4px;color:#0f172a;font-size:22px;font-weight:950;letter-spacing:-.04em;}
  .lr-filter-head button{border:1px solid #e2e8f0;background:#fff;color:#334155;border-radius:999px;min-height:38px;padding:0 13px;font-weight:900;}
  .lr-filter-panel h4{margin:16px 0 10px;color:#0f172a;font-size:14px;font-weight:950;}
  .lr-sheet-options{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;}
  .lr-sheet-options button{border:1px solid #e2e8f0;background:#fff;color:#334155;border-radius:18px;min-height:48px;padding:0 12px;text-align:left;font-weight:900;}
  .lr-sheet-options button.active{border-color:#99f6e4;background:#ecfdf5;color:#00675f;box-shadow:0 0 0 4px rgba(20,184,166,.10);}
  .lr-filter-actions{display:flex;gap:10px;margin-top:20px;position:sticky;bottom:0;background:linear-gradient(180deg,rgba(255,255,255,0),#fff 18%);padding-top:18px;}
  .lr-filter-actions button{flex:1;min-height:50px;border-radius:17px;font-weight:950;}
  .lr-filter-actions .secondary{border:1px solid #e2e8f0;background:#fff;color:#334155;}
  .lr-filter-actions .primary{border:0;background:#00675f;color:#fff;box-shadow:0 14px 28px rgba(0,103,95,.20);}

`;
